import { ApiAuthError } from './auth-api';

/**
 * PHI-safe error logger for the auth surface (NABIDH-relevant).
 *
 * Auth error responses can carry user identity in the body:
 *   - `/api/Account/CheckUserExistence` echoes the email/tenant
 *   - `/api/Account/LoginByCode` may include `passwordResetToken` + email
 *   - `/api/Account/UserDataLoginWithCode/{email}` echoes profile fields
 *     (firstName, lastName, emailAddress, photoUrl, roleId)
 *   - `/connect/token` 4xx bodies sometimes contain the username verbatim
 *
 * The `ApiAuthError.body` field stores the raw response text verbatim
 * for backend-side triage when an admin reads it back. **Never console
 * .log / Sentry / Mixpanel the body in client code** — browser DevTools
 * console is accessible to anyone on the device, and any future
 * console-bridge to a remote logger would silently leak PII/PHI.
 *
 * Per CLAUDE.md (healthcare compliance rules): "Never log PHI via
 * print(), debugPrint(), Sentry, Mixpanel, or Crashlytics."
 *
 * This helper logs ONLY:
 *   - the context label (caller-provided, no user data)
 *   - the HTTP status code (for triage)
 *   - the error class name (`ApiAuthError`, `Error`, etc)
 *
 * It does NOT log:
 *   - `err.body` (raw backend response — may include email / userId / tenant)
 *   - `err.message` (may include Flutter-verbatim user-facing strings —
 *     safe in isolation but defense-in-depth: don't expose to the console
 *     in case the message ever evolves to include user data)
 *   - the full err object (could include unknown future fields)
 *
 * If you need the full error for debugging, use the React-Query devtools
 * or a network-tab inspection — not the JS console.
 */
export function safeLogAuthError(context: string, err: unknown): void {
  let payload: { status?: number; name: string };
  if (err instanceof ApiAuthError) {
    payload = { status: err.status, name: 'ApiAuthError' };
  } else if (err instanceof Error) {
    payload = { name: err.name || 'Error' };
  } else {
    payload = { name: typeof err };
  }
  // eslint-disable-next-line no-console
  console.error(`[${context}]`, payload);
}
